import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import _get from 'lodash/get';
import _replace from 'lodash/replace';
import { FormTemplateQuestionFieldsKindEnum } from 'graphql-common';
import FormItem from '@lib/components/ReactHookForm/FormItem';
import CheckBoxSingleBool from '@lib/components/CheckBoxSingleBool/CheckBoxSingleBool';
import formStyles from '@lib/components/ReactHookForm/Form.module.scss';
import QuestionGeneralField from 'components/FormTemplateBuilder/QuestionGeneralField/QuestionGeneralField';
import QuestionCheckboxButtonGridField from 'components/FormTemplateBuilder/QuestionCheckboxButtonGridField/QuestionRadioButtonGridField';
import QuestionChecklistField from 'components/FormTemplateBuilder/QuestionChecklistField/QuestionChecklistField';
import {
  AppendValue,
  FieldArrayItemRenderAddMore,
  FieldArrayItemRenderEmptyState,
  FieldArrayItemType,
} from '@lib/components/ReactHookForm/types';
import FormTemplateFieldNames from '@lib/enums/fieldNames/formTemplateFieldNames';
import {
  kindFieldsWithAllowDisplayLastAnswer,
  kindFieldsWithResponseLimitByGroup,
  kindFieldsWithResponseLimitToOne,
} from 'components/FormTemplateBuilder/constants';
import Tooltip from '@lib/components/Tooltip/Tooltip';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import QuestionMinMaxField from '../QuestionMinMaxField/QuestionMinMaxField';

interface ChildrenProps {
  fieldItem: FieldArrayItemType;
  index: number;
}

interface Props {
  appendValue?: AppendValue;
  children: (props: ChildrenProps) => React.ReactNode;
  fields: FieldArrayItemType[];
  parentName: string;
  renderAddMore: FieldArrayItemRenderAddMore;
  renderEmptyState: FieldArrayItemRenderEmptyState;
}

function QuestionField(props: Props) {
  const {
    appendValue,
    children,
    fields,
    parentName,
    renderAddMore,
    renderEmptyState,
  } = props;
  const { t } = useTranslation();
  const { getValues } = useFormContext();
  const values = getValues();
  const kind = _get(
    values,
    _replace(
      parentName,
      `.${FormTemplateFieldNames.Options}`,
      `.${FormTemplateFieldNames.Kind}`,
    ),
    {},
  );

  const isAllowDisplayLastAnswerFieldVisible =
    kindFieldsWithAllowDisplayLastAnswer.includes(kind);
  const isAllowResponseLimits =
    kindFieldsWithResponseLimitByGroup.includes(kind);
  const isAllowResponseLimitsToOne =
    kindFieldsWithResponseLimitToOne.includes(kind);
  const allowDisplayLastAnswerField = isAllowDisplayLastAnswerFieldVisible ? (
    <FormItem
      name={_replace(
        parentName,
        FormTemplateFieldNames.Options,
        FormTemplateFieldNames.AllowDisplayLastAnswer,
      )}
      element={CheckBoxSingleBool}
      componentProps={{
        id: `${parentName}-${FormTemplateFieldNames.AllowDisplayLastAnswer}`,
        label: t('allow-last-statement'),
      }}
    />
  ) : undefined;
  const allowResponseLimits = isAllowResponseLimits ? (
    <>
      <FormItem
        name={_replace(
          parentName,
          FormTemplateFieldNames.Options,
          FormTemplateFieldNames.LimitToOneRespPerRow,
        )}
        element={CheckBoxSingleBool}
        componentProps={{
          id: `${parentName}-${FormTemplateFieldNames.LimitToOneRespPerRow}`,
          label: t('allow-limit-response-per-row'),
        }}
        hideErrorsSpace
      />
      <FormItem
        name={_replace(
          parentName,
          FormTemplateFieldNames.Options,
          FormTemplateFieldNames.LimitToOneRespPerColumn,
        )}
        element={CheckBoxSingleBool}
        componentProps={{
          id: `${parentName}-${FormTemplateFieldNames.LimitToOneRespPerColumn}`,
          label: t('allow-limit-response-per-col'),
        }}
      />
    </>
  ) : undefined;
  const allowResponseLimitsToOne = isAllowResponseLimitsToOne ? (
    <FormItem
      name={_replace(
        parentName,
        FormTemplateFieldNames.Options,
        FormTemplateFieldNames.LimitToOneResp,
      )}
      element={CheckBoxSingleBool}
      componentProps={{
        id: `${parentName}-${FormTemplateFieldNames.LimitToOneResp}`,
        label: t('allow-limit-response-to-one'),
        rightLabelIcon: (
          <Tooltip body={t('allow-limit-response-to-one-desc')}>
            <MaterialIcon icon="info" size="md-16" symbolsOutlined />
          </Tooltip>
        ),
      }}
      hideErrorsSpace
    />
  ) : undefined;
  const renderSettings =
    isAllowDisplayLastAnswerFieldVisible ||
    isAllowResponseLimits ||
    isAllowResponseLimitsToOne ? (
      <div className={formStyles.formItems}>
        {allowDisplayLastAnswerField}
        {allowResponseLimits}
        {allowResponseLimitsToOne}
      </div>
    ) : undefined;

  if (kind === FormTemplateQuestionFieldsKindEnum.MinMax) {
    return (
      <QuestionMinMaxField
        fields={fields}
        parentName={parentName}
        renderAddMore={renderAddMore}
        renderSettings={renderSettings}
        renderEmptyState={renderEmptyState}
      >
        {children}
      </QuestionMinMaxField>
    );
  }
  if (kind === FormTemplateQuestionFieldsKindEnum.Checklist) {
    return (
      <QuestionChecklistField
        fields={fields}
        parentName={parentName}
        renderAddMore={renderAddMore}
        renderSettings={renderSettings}
      >
        {children}
      </QuestionChecklistField>
    );
  }
  if (kind === FormTemplateQuestionFieldsKindEnum.CheckboxButtonGrid) {
    return (
      <QuestionCheckboxButtonGridField
        fields={fields}
        parentName={parentName}
        renderAddMore={renderAddMore}
        renderSettings={renderSettings}
      >
        {children}
      </QuestionCheckboxButtonGridField>
    );
  }
  return (
    <QuestionGeneralField
      appendValue={appendValue}
      fields={fields}
      kind={kind}
      renderAddMore={renderAddMore}
      renderSettings={renderSettings}
      renderEmptyState={renderEmptyState}
    >
      {children}
    </QuestionGeneralField>
  );
}

export default memo(QuestionField);
